import type { FetchOptions } from 'ofetch'
import { consola } from 'consola'
import { getApiTokenBridge, useAlertStore } from '@shared/model'
import { offlineManager } from '@shared/lib'
// Импорт напрямую из файла, а НЕ из barrel'а '@shared/config': barrel тянет
// константы модалок и UI-конфиги, из-за чего apiClient попадает в цикл импортов.
import { useVersion } from '@shared/config/version'
// Импорт напрямую из файла по той же причине, что и version выше.
import { getPlatform } from '@shared/lib/platform/capacitorDetector'
// Тоже напрямую из файла, а не из barrel'а '@shared/lib' — barrel уже тянется сюда
// ради offlineManager, и класть в него ещё и аналитику значит рисковать циклом.
import { getAnalyticsSessionId } from '@shared/lib/analytics/analyticsSession'
import { isAnalyticsEnabled } from '@shared/lib/analytics/analyticsFlag'
import { useRuntimeConfig } from '#imports'
import { OfflineError } from '~/shared/lib/errors/OfflineError'

const logger = consola.withTag('api')

/** Версия сборки — вшивается генератором на билде, за время жизни бандла не меняется. */
const APP_VERSION = useVersion().version

/**
 * Платформа клиента. Считается на КАЖДЫЙ запрос, а не константой уровня модуля
 * (как APP_VERSION выше): на SSR Capacitor ещё не поднят, и константа навсегда
 * застыла бы в 'web' — включая последующие запросы уже из нативной оболочки.
 */
const resolvePlatform = (): string => {
  if (!process.client) return 'web'

  try {
    return getPlatform()
  } catch {
    // Capacitor недоступен (обычный браузер до инициализации) — это и есть web.
    return 'web'
  }
}

const MAX_RETRY_ATTEMPTS = 1

const errorDebounce = new Map<string, number>()

/** Коды, которыми фоновый пересчёт заказа сообщает о составе корзины, а не о сбое. */
const CART_CONTENT_CODES = new Set([
  'PRODUCT_UNAVAILABLE',
  'PRODUCT_IN_STOP_LIST',
  'PRODUCT_NOT_FOUND',
])
/**
 * Ошибки адреса доставки в превью-расчёте: их показывает сам расчёт (useOrderPreview)
 * тостом с кнопкой «Указать на карте». Второй, общий красный тост с тем же текстом
 * только дублирует сообщение.
 */
const DELIVERY_ADDRESS_CODES = new Set([
  'DELIVERY_ADDRESS_REQUIRED',
  'DELIVERY_OUT_OF_ZONE',
  'DELIVERY_COORDS_REQUIRED',
  'DELIVERY_ADDRESS_INCOMPLETE',
  'DELIVERY_ADDRESS_UNCONFIRMED',
])
const DEBOUNCE_TIME = 5000

/**
 * Composable для работы с API.
 *
 * Возможности:
 * - Поддержка v1 / v2 API-префиксов
 * - Bearer-авторизация из localStorage (user / admin)
 * - Проактивное и реактивное (401-retry) обновление токена
 * - Offline-проверка и обработка сетевых ошибок
 * - Дебаунсированные алерты ошибок
 *
 * @param versionV2     - использовать /api/v2 (по умолчанию false = v1)
 * @param isAdminRequest - использовать admin-токен (по умолчанию false)
 *
 * @example
 * const api = useApi(true)            // /api/v2, user token
 * const api = useApi(true, true)      // /api/v2, admin token
 * const { data } = await api.get<Product[]>('/products')
 */
export const useApi = (versionV2 = false, isAdminRequest = false) => {
  const config = useRuntimeConfig()

  /** Тот же флаг, что включает отправку событий: заголовок без приёмника бессмыслен. */
  const analyticsOn = (): boolean => isAnalyticsEnabled(config.public.analyticsEnabled)

  const baseURL = versionV2
    ? `${config.public.baseUrl}${config.public.apiV2Prefix ?? ''}`
    : `${config.public.baseUrl}${config.public.apiPrefix ?? ''}`

  // --- Token helpers ---

  const getAccessToken = (): string | null => {
    if (!process.client) return null

    const key = isAdminRequest ? 'adminAccessToken' : 'authAccessToken'
    const token = localStorage.getItem(key)

    if (process.dev && token) {
      logger.debug(isAdminRequest ? '🔑 Admin token' : '🔑 User token')
    }

    return token
  }

  const isRefreshRequest = (url: string) => url.includes('/auth/refresh')

  // --- 401 handler (refresh token) ---

  const handleUnauthorized = (): Promise<boolean> => {
    if (!process.client) {
      logger.warn('Cannot refresh token during SSR')
      return Promise.resolve(false)
    }

    const bridge = getApiTokenBridge()
    if (!bridge) {
      logger.warn('ApiTokenBridge не зарегистрирован (ожидайте plugins/api-token-bridge)')
      return Promise.resolve(false)
    }

    return bridge.handleUnauthorized({ isAdminRequest })
  }

  // --- Proactive token refresh (before request) ---

  const proactiveTokenRefresh = async (url: string): Promise<void> => {
    if (!process.client || isRefreshRequest(url)) return

    const bridge = getApiTokenBridge()
    if (!bridge) return

    try {
      await bridge.proactiveBeforeRequest({ url })
    } catch {
      // Proactive refresh failure is non-critical — request proceeds, 401 retry handles the rest
    }
  }

  // --- Core request function ---

  const request =
    (method: FetchOptions['method'], _retryCount = 0) =>
    async <T = any>(
      url: string,
      options: FetchOptions = {},
    ): Promise<{ status: number; data: T }> => {
      if (_retryCount === 0) {
        await proactiveTokenRefresh(url)
      }

      if (process.client && offlineManager.isOffline()) {
        logger.warn('Request blocked: app is offline', url)
        throw new OfflineError()
      }

      const fullUrl = `${baseURL}${url}`
      const accessToken = getAccessToken()

      const headers: Record<string, string> = {
        // Версия клиента едет с КАЖДЫМ запросом.
        // Зачем: фронт зашит в бандл приложения, старые версии живут на устройствах,
        // и без этого заголовка сервер не может ни измерить их долю, ни отличить
        // старого клиента — а значит удалить любое устаревшее поле ответа нельзя
        // никогда. Бэк заголовок уже читает (device-info.util.ts).
        'X-App-Version': APP_VERSION,
        // Версия бандла одна на iOS, Android и веб, поэтому сама по себе она не
        // адресует платформу. Пара версия+платформа — адресует.
        'X-App-Platform': resolvePlatform(),
        // Идентификатор аналитической сессии. Нужен, чтобы серверные события (обмен
        // токенов при входе, webhook оплаты) склеились с клиентской воронкой: шаг
        // «сервис вернул токен» виден только бэку, а шаг «нажал кнопку входа» — только фронту.
        //
        // Под флагом НЕ ради аналитики, а ради выживания клиента: список allowedHeaders
        // в CORS закрытый, и пока бэк с этим заголовком не выкачен, браузер режет на
        // preflight ВСЕ запросы, а не только аналитические. Флаг включаем после бэка.
        ...(process.client && analyticsOn()
          ? { 'X-Analytics-Session': getAnalyticsSessionId() }
          : {}),
        ...(options.headers as Record<string, string>),
      }

      if (accessToken && !isRefreshRequest(url)) {
        headers.Authorization = `Bearer ${accessToken}`
      }

      if (options.body && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'
      }

      try {
        if (process.dev && _retryCount === 0) {
          logger.debug(`${method} ${url}`, { hasAuth: !!accessToken, hasBody: !!options.body })
        }

        const response = await $fetch.raw(fullUrl, {
          ...options,
          method,
          headers,
          credentials: 'include',
        })

        if (process.dev) {
          logger.success(`${method} ${url} → ${response.status}`)
        }

        return { status: response.status, data: response._data as T }
      } catch (fetchError: any) {
        const status: number | undefined = fetchError?.response?.status ?? fetchError?.status

        if (!status && process.client) {
          const offlineError = OfflineError.fromFetchError(fetchError)
          offlineManager.emit('networkError')
          handleApiError(offlineError, url)
          throw offlineError
        }

        if (status === 401 && _retryCount < MAX_RETRY_ATTEMPTS && !isRefreshRequest(url)) {
          logger.warn(`401 on ${url}, refreshing token (attempt ${_retryCount + 1}/${MAX_RETRY_ATTEMPTS})`)

          const refreshed = await handleUnauthorized()

          if (refreshed) {
            return request(method, _retryCount + 1)<T>(url, options)
          }
        }

        handleApiError(fetchError, url)
        throw fetchError
      }
    }

  // --- Error display (debounced) ---

  const handleApiError = (error: any, url: string): void => {
    const status: number | undefined = error?.response?.status ?? error?.status
    const responseData = error?.response?._data ?? error?.data
    const message: string =
      responseData?.message ?? responseData?.error ?? error?.message ?? 'Неизвестная ошибка'

    if (OfflineError.isOfflineError(error)) {
      logger.warn('Offline error:', url, message)
      return
    }

    if (status === 401) {
      logger.warn('401 Unauthorized:', url)
      return
    }

    if (status === 403) {
      if (isRefreshRequest(url)) {
        logger.error('403: Refresh token invalid')
        return
      }
      if (process.client && sessionStorage.getItem('payment_processing') === 'true') {
        logger.warn('403: Ignored during payment processing')
        return
      }
      if (url.includes('/referral/my-code') && responseData?.code === 'INSUFFICIENT_ORDERS') {
        logger.warn('403: Insufficient orders for referral — suppressed')
        return
      }
      logger.warn('403 Forbidden:', url)
    }

    if (status && status !== 401 && status !== 403) {
      logger.error(`${status} Error:`, { url, message, status })
    }

    if (shouldSuppressAlert(message, responseData, status, url)) return

    const errorKey = `${status}-${message}`
    const lastShown = errorDebounce.get(errorKey) ?? 0
    const now = Date.now()

    if (now - lastShown < DEBOUNCE_TIME) {
      logger.debug('Error alert debounced:', errorKey)
      return
    }

    errorDebounce.set(errorKey, now)
    useAlertStore().error(message)
  }

  const shouldSuppressAlert = (
    message: string,
    responseData: any,
    status: number | undefined,
    url: string,
  ): boolean => {
    if (status === 403 && responseData?.code === 'INSUFFICIENT_ORDERS') return true

    // Превью-расчёт (/orders/calculate) пересчитывается фоном на каждое изменение
    // корзины, филиала и адреса. Недоступность позиции — это не сбой запроса:
    // корзина уже помечает такую карточку «Товар недоступен», а оформление
    // заказа отдельно отобьёт её понятной ошибкой. Глобальный красный тост здесь
    // только пугает — тем более что при смене филиала позиция через миг заменится
    // карточкой этого филиала, и ошибки уже нет.
    if (url.includes('/orders/calculate') && CART_CONTENT_CODES.has(responseData?.code)) {
      return true
    }
    if (url.includes('/orders/calculate') && DELIVERY_ADDRESS_CODES.has(responseData?.code)) {
      return true
    }

    const suppressedPhrases = ['Некорректный ID филиала']
    return suppressedPhrases.some(phrase => message.includes(phrase))
  }

  return {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    patch: request('PATCH'),
    delete: request('DELETE'),
  }
}
